import {FC, useMemo, useState} from "react";
import classnames from "classnames/bind";
import {Dayjs} from "dayjs";

import {InputComponent} from "..";
import {Span} from "../../text";

import styles from "./date-picker-multiple.module.scss";
const bStyles = classnames.bind(styles);

const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

export interface DateRangePickerProps extends InputComponent<[Dayjs, Dayjs | undefined]> {
	min?: Dayjs;
	max?: Dayjs;
	displayMonth: Dayjs;
}

export const DateRangePicker: FC<DateRangePickerProps> = ({value, onChange, min, max, displayMonth}) => {
	const weeks = useMemo(() => displayMonth.getWeeksInRange(), [displayMonth]);
	const startDate = value[0];
	const endDate = value[1];
	const [hoverValue, setHoverValue] = useState<Dayjs>();
	const handleChange = (day: Dayjs) => {
		if (value[1]) onChange([day.startOf("day"), undefined]);
		else {
			if (value[0] < day.startOf("day")) {
				onChange([value[0], day.endOf("day")]);
			} else {
				onChange([day.startOf("day"), value[0].endOf("day")]);
			}
		}
	};

	const week = week =>
		week.days.map(day => {
			const d = day.format("DD");

			const canClick =
				(min ? day.isSameOrAfter(min, "day") : true) && (max ? day.isSameOrBefore(max, "day") : true);
			const startDay = startDate.isSame(day, "day");
			const endDay = endDate && endDate.isSame(day, "day");

			const betweenDates =
				endDate &&
				day > (startDate < endDate ? startDate : endDate) &&
				day < (startDate > endDate ? startDate : endDate);
			const hoverDay = hoverValue && hoverValue.isSame(day, "day");

			const betweenHover =
				!endDate &&
				hoverValue &&
				day > (startDate < hoverValue ? startDate : hoverValue) &&
				day < (startDate > hoverValue ? startDate : hoverValue);

			return (
				<div
					tabIndex={canClick ? 0 : undefined}
					key={day.unix()}
					onMouseOver={() => setHoverValue(day)}
					onMouseOut={() => setHoverValue(undefined)}
					className={bStyles("weekContainer", {
						weekContainer: true,
						startDay,
						endDay,
						hoverDay,
						halfDottedLeft:
							!endDate &&
							hoverValue &&
							(startDay ? hoverValue < startDate : hoverDay ? hoverValue > startDate : false),
						halfDottedRight:
							!endDate &&
							hoverValue &&
							(startDay ? hoverValue > startDate : hoverDay ? hoverValue < startDate : false),
						hideBefore: !endDate ? startDay : endDate.startOf("day").isSame(startDate.startOf("day")),
						betweenHover,
						betweenDates,
						disabledDay: !canClick || !displayMonth.isSame(day, "month"),
					})}
					onClick={() => handleChange(day)}
					onKeyDown={e => (e.key === "Enter" || e.key === " ") && handleChange(day)}
				>
					<Span>{d}</Span>
				</div>
			);
		});

	return (
		<div className={styles.datePicker}>
			{days.map(day => (
				<div className={styles.dayContainer} key={day}>
					<Span color="grey">{day}</Span>
				</div>
			))}
			{weeks.map(week)}
		</div>
	);
};
